import { ChangeEvent, useCallback, useState } from 'react';
import { addHours } from 'date-fns';
import { noop } from 'lodash-es';
import { Radio, RadioGroup, SearchInput } from '@ds';
import { FilterDropdown } from '@ui';
import { SurgeonsTable } from '@lp/features/AssignmentTables';
import {
  OpenAssignmentCalendarButton,
  OrDateTimePicker,
} from '@lp/features/CaseOverview/components/Tabs/AssignedResources/components/AssignResourcesModal/components';
import {
  AffiliationType,
  EmployeeRole,
  RSAssignmentCandidate,
  RSContractorAssignmentCandidate,
} from '@/api/apis/resources';
import { OrganType } from '@/api/apis/case';
import { useCaseDetails } from '@/hooks/useCaseDetails';
import { CONTRACTORS_AFFILIATION_SELECT_LIST } from './config';
import { getTimeZonedDate } from '@/utils/helpers';
import { contractorAffiliationNames } from '@/constants/resources.constants';

export type RoutePlannerSurgeonListProps = {
  organType: OrganType;
  hideControls?: boolean;
  onAction?: (data: RSContractorAssignmentCandidate | RSAssignmentCandidate, cb: () => void) => void;
  onConfirmation?: (data: RSContractorAssignmentCandidate | RSAssignmentCandidate) => void;
  selectedResourceId?: string;
  isContractor?: boolean;
  surgeonAffiliationType?: AffiliationType;
};

export const RoutePlannerSurgeonList = ({
  organType,
  hideControls,
  onAction,
  onConfirmation,
  selectedResourceId,
  isContractor = false,
  surgeonAffiliationType,
}: RoutePlannerSurgeonListProps) => {
  const [searchValue, setSearchValue] = useState('');

  const [isContractorSelected, setIsContractorSelected] = useState(isContractor);
  const [affiliationType, setAffiliationType] = useState<AffiliationType | undefined>(surgeonAffiliationType);
  const { caseId, caseName, donorType, donorHospitalDate, donorHospitalTime, donorHospitalTimeZone } = useCaseDetails();
  const orCaseDateTime = getTimeZonedDate(donorHospitalDate, donorHospitalTime, donorHospitalTimeZone);

  const handleReserve = useCallback(
    (item: RSContractorAssignmentCandidate | RSAssignmentCandidate) => {
      onAction?.(item, () => onConfirmation?.(item));
    },
    [onAction, onConfirmation],
  );

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  const onTabChange = (val: string) => {
    setIsContractorSelected(val === 'true');
  };

  const handleAffiliationTypeChange = (val: string) => {
    setAffiliationType(val as AffiliationType);
  };

  const orDateTime = getTimeZonedDate(donorHospitalDate, donorHospitalTime, donorHospitalTimeZone);
  const orTimeCorrected = orDateTime?.toAbsoluteString() ?? addHours(new Date(), 6).toISOString();

  return (
    <div className="flex flex-col place-items-stretch gap-6">
      {!hideControls && (
        <div className="flex justify-between">
          <div className="flex items-center gap-6">
            <RadioGroup
              className="p-0"
              value={isContractorSelected.toString()}
              onValueChange={onTabChange}
              orientation="horizontal"
              classNames={{
                wrapper: 'gap-6',
              }}
            >
              <Radio size="md" value="false">
                Employee
              </Radio>
              <Radio size="md" value="true">
                Contractor
              </Radio>
            </RadioGroup>
            {isContractorSelected && (
              <FilterDropdown
                title={affiliationType ? contractorAffiliationNames[affiliationType] : 'Affiliation Type'}
                items={CONTRACTORS_AFFILIATION_SELECT_LIST}
                selectedKeys={affiliationType ? [affiliationType] : []}
                mode="single"
                size="sm"
                showCounter={false}
                onSelectedKeysChange={(values) => {
                  handleAffiliationTypeChange(values[0] as AffiliationType);
                }}
              />
            )}
            <OrDateTimePicker
              showOrTypeSwitch={false}
              orDateTime={orCaseDateTime!}
              onOrDateTimeChange={noop}
              isCustom={false}
              onIsCustomChange={noop}
            />
          </div>
          {!isContractorSelected && (
            <OpenAssignmentCalendarButton
              day={orTimeCorrected}
              employeeRole={EmployeeRole.RetrievalSurgeon}
              organType={organType}
            />
          )}
        </div>
      )}
      <SearchInput placeholder="Search" value={searchValue} onChange={onSearchChange} />
      <div className="flex flex-col">
        <SurgeonsTable
          isContractor={isContractorSelected}
          affiliation={affiliationType}
          searchTerm={searchValue}
          orTime={orTimeCorrected}
          caseId={caseId}
          organ={organType}
          donor={donorType}
          isLogistics
          selectedSurgeonsIds={selectedResourceId ? [selectedResourceId] : []}
          onContractorReserve={handleReserve}
          onEmployeeReserve={handleReserve}
          caseName={caseName}
        />
      </div>
    </div>
  );
};
